import "maplibre-gl/dist/maplibre-gl.css";

import { MapController, MapView } from "@deck.gl/core";
import DeckGL from "@deck.gl/react";
import type { GeoJSON } from "geojson";
import { Menu, X } from "lucide-react";
import { forwardRef, type ReactNode, useEffect, useImperativeHandle, useRef, useState } from "react";
import * as maplibre from "react-map-gl/maplibre";

import { Button } from "~/components/ui";
import { mapStyle } from "~/lib/map/components/constants";
import { useEditMap } from "~/lib/map/hooks";
import type {
  ComparisonArea,
  CoordsType,
  Location,
  PinnedLocation,
  ResolutionType,
  ViewportType,
} from "~/lib/map/types";

const MAP_HEIGHT = 560;

export interface EditMapHandles {
  moveTo: (coords: CoordsType) => void;
}

interface EditMapProps {
  resolution: ResolutionType;
  areaH3Indexes: string[];
  geometry: GeoJSON | null;
  enableHexagonSelection?: boolean;
  onHexagonClick: (h3Index: string) => void;
  onLocationClick?: (location: Location) => void;
  onViewPortChange?: (viewport: {
    latitude: number;
    longitude: number;
    zoom: number;
    width: number;
    height: number;
  }) => void;
  comparisonAreas?: ComparisonArea[];
  pinnedLocations?: PinnedLocation[];
  searchedLocation?: Location | null;
  inViewportLocations?: Location[];
  /** Optional docked tools panel (search / pinned / comparison areas). */
  tools?: ReactNode;
}

/**
 * Interactive area-drawing map (V2, MUI-free). Reuses the shared `useEditMap`
 * (H3 hexagon-select grid + comparison/pinned/location layers) over a maplibre base.
 */
export const EditMap = forwardRef<EditMapHandles, EditMapProps>(function EditMap(props, ref) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [toolsOpen, setToolsOpen] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden rounded-[var(--radius-md)] border border-border-default"
      style={{ height: MAP_HEIGHT }}
    >
      {/* The H3 grid is derived from the viewport bounds; rendering before the width is
          measured produces a degenerate viewport that crashes h3 `polygonToCells`. */}
      {width > 0 && <EditMapCanvas ref={ref} width={width} {...props} />}

      {props.tools && (
        <>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="absolute right-2 top-2 z-[5] bg-bg-surface"
            aria-label={toolsOpen ? "close" : "open"}
            onClick={() => setToolsOpen((o) => !o)}
          >
            {toolsOpen ? <X /> : <Menu />}
          </Button>
          {toolsOpen && (
            <div className="absolute right-2 top-14 z-[5] max-h-[calc(560px-72px)] w-[300px] overflow-y-auto rounded-[var(--radius-md)] border border-border-default bg-bg-surface shadow-md">
              {props.tools}
            </div>
          )}
        </>
      )}
    </div>
  );
});

const EditMapCanvas = forwardRef<EditMapHandles, EditMapProps & { width: number }>(function EditMapCanvas(
  {
    width,
    resolution,
    areaH3Indexes,
    geometry,
    enableHexagonSelection,
    onHexagonClick,
    onLocationClick = () => {},
    onViewPortChange = () => {},
    comparisonAreas,
    pinnedLocations,
    searchedLocation,
    inViewportLocations,
  },
  ref,
) {
  const { layers, viewState, onViewStateChange, setViewState } = useEditMap({
    width,
    height: MAP_HEIGHT,
    resolution,
    areaH3Indexes,
    geometry,
    enableHexagonSelection: enableHexagonSelection ?? (areaH3Indexes.length > 0 || !geometry),
    onHexagonClick,
    onLocationClick,
    comparisonAreas,
    pinnedLocations,
    searchedLocation: searchedLocation ?? undefined,
    inViewportLocations,
  });

  useImperativeHandle(ref, () => ({
    moveTo: (coords: CoordsType) =>
      setViewState((prev: ViewportType) => ({ ...prev, latitude: coords.lat, longitude: coords.lng })),
  }));

  // Fly to a freshly-searched location.
  useEffect(() => {
    if (!searchedLocation) return;
    setViewState((prev: ViewportType) => ({
      ...prev,
      latitude: searchedLocation.coords.lat,
      longitude: searchedLocation.coords.lng,
    }));
  }, [searchedLocation, setViewState]);

  // Report the viewport so the tools can run in-viewport discovery. Read the callback from a
  // ref so this effect fires only on a real viewState/width change — NOT on every render.
  // Depending on `onViewPortChange` (a non-memoized prop) re-fires it each render and, with
  // in-viewport discovery active, creates a query→re-render→setViewport→query loop (memory leak).
  // Matches the baseline `lib/map` EditMap, which depends only on [viewState].
  const onViewPortChangeRef = useRef(onViewPortChange);
  onViewPortChangeRef.current = onViewPortChange;
  useEffect(() => {
    onViewPortChangeRef.current({
      latitude: viewState.latitude ?? 0,
      longitude: viewState.longitude ?? 0,
      zoom: viewState.zoom ?? 0,
      width,
      height: MAP_HEIGHT,
    });
  }, [viewState, width]);

  return (
    <DeckGL
      id="area-edit-map"
      // Uncontrolled view (like V1 `lib/map` EditMap): DeckGL drives the live drag itself.
      // A controlled `viewState` freezes panning because the shared `useMapBase.onViewStateChange`
      // skips updates while `isDragging`. `setViewState` (moveTo / fly-to-search) still re-centers.
      initialViewState={viewState}
      onViewStateChange={onViewStateChange}
      layers={layers}
      views={new MapView({ repeat: true })}
      controller={{ type: MapController, dragRotate: false, doubleClickZoom: false }}
      width="100%"
      height={MAP_HEIGHT}
    >
      <maplibre.Map key="area-edit-map" mapStyle={mapStyle} />
    </DeckGL>
  );
});
